import React, { PropsWithChildren, useState } from 'react';
import styled, { css } from 'styled-components';

import { WIZARD_COMPONENT } from '../../../shared/constants';

import ChevronIconAnimation from '../../atoms/icons/ChevronIconAnimation';

import { DEQ_TYPES } from '../../../utils/deq';

import mq from '../../../styles/breakpoints';
import colors from '../../../styles/colors';
import { Body, H4 } from '../../../styles/generic';
import Spacings from '../../../styles/Spacings';

export interface AccordionItemProps extends PropsWithChildren {
  title: string;
  type?: string;
  animateItem?: boolean;
}

const AccordionItem = ({
  title,
  children,
  type,
  animateItem = true,
}: AccordionItemProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleOnChange = () => {
    setIsOpen(!isOpen);
  };

  return (
    <Container
      data-component-type="AccordionItem"
      data-element-type={DEQ_TYPES.foldout}
      data-element-label={title.toLowerCase()}
      data-testid="accordion-item-container"
      onClick={handleOnChange}
      role="button"
    >
      <Title type={type}>
        <StyledH4 aria-level={3}>{title}</StyledH4>
        <ChevronIconAnimation open={isOpen} animateTransition={animateItem} />
      </Title>
      <ListItem
        data-testid="accordion-item-content"
        open={isOpen}
        type={type}
        animateTransition={animateItem}
      >
        <StyledBody>{children}</StyledBody>
      </ListItem>
    </Container>
  );
};

export default AccordionItem;

const Container = styled.ul`
  cursor: pointer;
  margin: 0;
  max-width: inherit;
  padding: 0;

  &:not(:last-of-type) {
    border-bottom: 1px solid ${colors.abnNeutrals75};
  }
`;

const Title = styled.div<{ type?: string }>`
  align-items: center;
  color: ${({ theme }) => theme.colors.abnGreen300};
  display: flex;
  flex-direction: row;
  justify-items: center;
  padding: ${Spacings.S}; 0;

  ${mq.from.breakpoint.M`
    &:hover {
      color: ${({ theme }) => theme.colors.abnGreen200};
    }
  `}

  ${({ type }) =>
    type === WIZARD_COMPONENT.wizardFaqItem &&
    css`
      margin-top: ${Spacings.M};
      padding: ${Spacings.S};

      ${mq.from.breakpoint.L`
        &:hover {
          background-color: ${colors.abnGreen50};
        }
      `}

      & > h4 {
        font-size: ${Spacings.S};
        line-height: 1;
      }
    `}
`;

const StyledH4 = styled(H4)`
  flex-grow: 1;
  margin: 0 32px 0 0;
`;

const ListItem = styled.li<{
  open: boolean;
  type?: string;
  animateTransition?: boolean;
}>`
  list-style: none;
  margin: 0;
  max-height: 0;
  overflow: hidden;
  padding: 0;

  ${({ open }) => open && 'max-height: 2000px; min-height: 90%;'}

  ${({ animateTransition }) =>
    animateTransition && 'transition: max-height 0.3s ease-in-out;'}

  ${({ type }) =>
    type === WIZARD_COMPONENT.wizardFaqItem
      ? css`
          margin-left: ${Spacings.S};
        `
      : css`
          margin-left: 0;
        `}
`;

const StyledBody = styled(Body)`
  padding-bottom: ${Spacings.S};
`;
